import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigation } from "lucide-react";
import "./NextStopsList.scss";
import { useDriverPlannedRoute } from "../views/adminView/actividad/useDriverPlannedRoute";
import { OriginIcon } from "./OriginIcon";

/**
 * Orden en que el repartidor seleccionado va a visitar lo que le queda, y a qué hora.
 *
 * Es la pregunta que el panel no podía contestar: mostraba dónde está y qué hizo, pero no
 * hacia dónde va. La traza en el mapa ya se dibuja aparte; esto es la lista numerada, que es
 * lo que se puede leer por teléfono cuando un cliente pregunta "¿a qué hora me llega?".
 *
 * El orden sale del propio teléfono del repartidor (lo publica al optimizar), así que refleja
 * los reordenamientos por zonas y las paradas que agregó a mano. En sesiones viejas —las que
 * todavía calculó el worker server-side— solo hay ids de paquete, sin dirección: ahí se
 * muestra el número de orden y se dice que falta el detalle, en vez de inventar una etiqueta.
 *
 * La hora la calcula el backend contra la POSICIÓN VIVA del repartidor, no contra la agenda de
 * la última optimización: una agenda estática no sabe si va atrasado. Por eso el bloque siempre
 * declara su frescura — un ETA sin fecha de cálculo es el que después nadie sabe si creer.
 */

/** Cada cuánto se re-renderiza solo para envejecer la etiqueta de frescura. */
const FRESHNESS_TICK_MS = 30_000;

/**
 * Minutos transcurridos desde `iso`, recalculados solos.
 *
 * Existe el tick propio porque el dato se refresca cada 60 s pero la etiqueta tiene que envejecer
 * entremedio: sin esto diría "recién" durante un minuto entero y después saltaría.
 */
function useMinutesSince(iso) {
  const [minutes, setMinutes] = useState(() => minutesSince(iso));

  useEffect(() => {
    setMinutes(minutesSince(iso));
    if (!iso) return undefined;

    const timer = setInterval(() => setMinutes(minutesSince(iso)), FRESHNESS_TICK_MS);
    return () => clearInterval(timer);
  }, [iso]);

  return minutes;
}

function minutesSince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 60000));
}

function formatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Umbral a partir del cual la antigüedad del GPS se declara aparte de la del cálculo.
 *
 * Debajo de esto son la misma cosa en la práctica y decir las dos solo agrega ruido; por encima,
 * esconderla haría pasar por fresco un número calculado sobre una posición vieja.
 */
const STALE_POSITION_NOTICE_MIN = 2;

export default function NextStopsList({ driverId, paradas }) {
  const { t } = useTranslation();
  const { nextStops, stopEtas, etaMeta } = useDriverPlannedRoute(driverId);

  const calcMinutes = useMinutesSince(etaMeta?.calculatedAt);
  const positionMinutes = useMinutesSince(etaMeta?.positionAsOf);

  // Índice paquete → origen, para pintar el logo del canal en cada parada que
  // queda por visitar. El orden lo publica el teléfono del repartidor y sólo
  // trae ids; el origen viene en el payload de la ruta, así que el cruce se
  // hace acá en el navegador y no cuesta un request más.
  //
  // Las paradas que el repartidor agregó a mano no tienen paquete asociado
  // (`packageId` null) y se quedan sin logo — es lo correcto: no hay pedido
  // detrás. Lo mismo para un id que no esté en la ruta del día.
  const origenPorPaquete = useMemo(() => {
    const index = new Map();
    for (const parada of paradas ?? []) {
      if (parada?.pkgId && parada.origen) index.set(String(parada.pkgId), parada.origen);
    }
    return index;
  }, [paradas]);

  // `null` = sin ruta activa, sin optimizar, o la sesión no expone orden. No se ocupa espacio
  // con un bloque vacío: el resto del panel sigue siendo útil.
  if (!nextStops) return null;

  if (nextStops.length === 0) {
    return (
      <div className="lt-next-stops lt-next-stops--empty">
        <span className="lt-next-stops__title">
          <Navigation size={13} /> {t('Próximas paradas')}
        </span>
        <p className="lt-next-stops__hint">{t('No le quedan paradas pendientes.')}</p>
      </div>
    );
  }

  // Por qué no hay ninguna hora. Se dice el motivo concreto en vez de dejar la columna muda:
  // "sin señal" y "todavía no optimiza" se resuelven de formas distintas.
  const reasons = {
    route_not_published: t('la ruta todavía no se optimiza'),
    no_stop_coords: t('falta la ubicación de la próxima parada'),
    no_signal: t('el repartidor no tiene señal'),
    stale_position: t('su última posición es demasiado antigua'),
    engine_unavailable: t('el motor de rutas no responde'),
  };
  const reason = etaMeta?.unavailableReason
    ? reasons[etaMeta.unavailableReason]
    : null;

  const freshness =
    calcMinutes == null
      ? null
      : calcMinutes < 1
        ? t('calculado recién')
        : t('calculado hace {{n}} min', { n: calcMinutes });

  // La antigüedad del GPS se declara aparte de la del cálculo a propósito: un ETA recalculado
  // hace 30 segundos sobre una posición de hace 8 minutos NO es un ETA de hace 30 segundos.
  const positionNotice =
    positionMinutes != null && positionMinutes >= STALE_POSITION_NOTICE_MIN
      ? t('posición de hace {{n}} min', { n: positionMinutes })
      : null;

  return (
    <div className="lt-next-stops">
      <span className="lt-next-stops__title">
        <Navigation size={13} />{' '}
        {t('Próximas paradas ({{n}})', { n: nextStops.length })}
      </span>
      <ol className="lt-next-stops__list">
        {nextStops.map((s) => {
          const etaAt = stopEtas?.[s.stopId];
          const time = etaAt ? formatTime(etaAt) : null;
          const origen = s.packageId ? origenPorPaquete.get(String(s.packageId)) : null;

          return (
            <li key={s.stopId} className="lt-next-stops__item">
              <span className="lt-next-stops__order">{s.order}</span>
              {origen && (
                <OriginIcon type={origen} size={13} className="lt-next-stops__origin" />
              )}
              <span className="lt-next-stops__label">
                {s.label || (
                  <em className="lt-next-stops__unknown">{t('sin dirección publicada')}</em>
                )}
              </span>
              {time && <span className="lt-next-stops__eta">{time}</span>}
            </li>
          );
        })}
      </ol>
      {(freshness || reason) && (
        <p className="lt-next-stops__freshness">
          {reason ? (
            <>{t('Sin hora estimada: {{motivo}}.', { motivo: reason })}</>
          ) : (
            [freshness, positionNotice].filter(Boolean).join(' · ')
          )}
        </p>
      )}
    </div>
  );
}
